import type { ParseKeys } from "i18next";

import { ModalComponent, ModalName } from "./modal-types";

/**
 * Modals register at import time, long before a React tree exists, so a title
 * cannot be translated where it is declared. The registry therefore stores the
 * translation KEY and `ModalProvider` resolves it at render — which is also why
 * the key is namespace-qualified: a modal's title belongs to the module that
 * registers it, not to the shared modal layer.
 */
export const MODAL_TITLE_NAMESPACES = ["common", "machines", "mlExports"] as const;

export type ModalTitleKey = ParseKeys<typeof MODAL_TITLE_NAMESPACES>;

interface ModalRegistryEntry<T extends ModalName> {
  component: ModalComponent<T>;
  maxWidth?: number;
  titleKey?: ModalTitleKey;
}

const modalRegistry = new Map<ModalName, ModalRegistryEntry<any>>();

export const registerModal = <T extends ModalName>(
  name: T,
  component: ModalComponent<T>,
  options?: { maxWidth?: number; titleKey?: ModalTitleKey }
) => {
  modalRegistry.set(name, {
    component,
    maxWidth: options?.maxWidth,
    titleKey: options?.titleKey,
  });
};

export const getModalComponent = <T extends ModalName>(
  name: T,
): ModalComponent<T> | undefined => {
  return modalRegistry.get(name)?.component;
};

export const getModalMaxWidth = <T extends ModalName>(
  name: T,
): number | undefined => {
  return modalRegistry.get(name)?.maxWidth;
};

export const getModalTitleKey = <T extends ModalName>(
  name: T,
): ModalTitleKey | undefined => {
  return modalRegistry.get(name)?.titleKey;
};
